import React, { useEffect, useMemo, useState } from 'react';
import { Tag, ChevronDown, ChevronRight, Plus, Trash2, RefreshCw, Undo2, RotateCcw } from 'lucide-react';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import axiosInstance from '../utils/axiosInstance';

const Categories = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('api/replai/categories/');
      setItems(data);
      // Auto-expand the top 3 busiest buckets on first load.
      setExpanded((prev) => {
        if (Object.keys(prev).length > 0) return prev;
        const openMap = {};
        data.slice(0, 3).forEach((d) => { openMap[d.name] = true; });
        return openMap;
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const reclassifyAll = async () => {
    setReclassifying(true);
    try {
      await axiosInstance.post('api/replai/reclassify-all/');
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setReclassifying(false);
    }
  };

  const resetToSeeds = async () => {
    const ok = window.confirm(
      'Reset all seed rules back to the Python baseline? Your manually-added rules will be kept.'
    );
    if (!ok) return;
    setResetting(true);
    try {
      await axiosInstance.post('api/replai/rules/reset-to-seeds/');
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (name) =>
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));

  const addRule = async (category, kind) => {
    const payload = {
      category,
      kind,
      required: [],
      boost: [],
      active: true,
    };
    try {
      await axiosInstance.post('api/replai/rules/', payload);
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const updateRule = async (id, patch) => {
    try {
      await axiosInstance.patch(`api/replai/rules/${id}/`, patch);
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const deleteRule = async (id) => {
    const ok = window.confirm('Delete this rule?');
    if (!ok) return;
    try {
      await axiosInstance.delete(`api/replai/rules/${id}/`);
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const header = (
    <div className="flex items-center gap-2">
      <Button onClick={reclassifyAll} disabled={reclassifying} variant="outline" className="gap-2">
        <RefreshCw className={`h-4 w-4 ${reclassifying ? 'animate-spin' : ''}`} />
        {reclassifying ? 'Reclassifying…' : 'Reclassify all'}
      </Button>
      <Button onClick={resetToSeeds} disabled={resetting} variant="outline" className="gap-2">
        <RotateCcw className={`h-4 w-4 ${resetting ? 'animate-spin' : ''}`} />
        Reset seeds
      </Button>
    </div>
  );

  return (
    <AppShell
      title="Categories"
      subtitle="Live rule mapping - edit rules from the UI, reclassify all tickets, or reset seeds to the Python baseline."
      headerRight={header}
    >
      <div className="space-y-2">
        {loading && items.length === 0 && <p className="text-sm text-muted-foreground">Loading…</p>}
        {items.map((cat) => {
          const isOpen = Boolean(expanded[cat.name]);
          return (
            <div
              key={cat.name}
              className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggle(cat.name)}
                className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-[hsl(var(--secondary)/0.4)] transition"
              >
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <Tag className="h-4 w-4 text-[hsl(var(--primary))] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="font-display text-lg font-semibold text-foreground">
                      {cat.name}
                    </h3>
                    <span className="text-xs text-muted-foreground">
                      {cat.ticket_count} ticket{cat.ticket_count === 1 ? '' : 's'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5 truncate">
                    {cat.description}
                  </p>
                </div>
                <span className="text-[0.65rem] text-muted-foreground shrink-0">
                  {cat.rules.length} body · {cat.subject_fallbacks.length} subject
                </span>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 pt-1 border-t border-border/70 space-y-4">
                  <RuleSection
                    title="Body rules"
                    kind="body"
                    rules={cat.rules}
                    category={cat.name}
                    onAdd={addRule}
                    onUpdate={updateRule}
                    onDelete={deleteRule}
                  />
                  <RuleSection
                    title="Subject fallbacks"
                    kind="subject"
                    rules={cat.subject_fallbacks}
                    category={cat.name}
                    onAdd={addRule}
                    onUpdate={updateRule}
                    onDelete={deleteRule}
                    subject
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AppShell>
  );
};

const SOURCE_COLORS = {
  seed:     'bg-stone-100 text-stone-700',
  manual:   'bg-emerald-50 text-emerald-700',
  proposed: 'bg-amber-50 text-amber-700',
};

const RuleSection = ({ title, kind, rules, category, onAdd, onUpdate, onDelete, subject = false }) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">
          {title} ({rules.length})
        </p>
        <button
          type="button"
          onClick={() => onAdd(category, kind)}
          className="inline-flex items-center gap-1 text-[0.65rem] rounded-lg px-2 py-1 text-muted-foreground hover:bg-[hsl(var(--secondary))] hover:text-foreground"
        >
          <Plus className="h-3 w-3" />
          Add rule
        </button>
      </div>
      {rules.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No {subject ? 'subject fallbacks' : 'body rules'} yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {rules.map((r) => (
            <RuleRow
              key={r.id}
              rule={r}
              subject={subject}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const RuleRow = ({ rule, subject, onUpdate, onDelete }) => {
  const [requiredInput, setRequiredInput] = useState((rule.required || []).join(', '));
  const [boostInput, setBoostInput] = useState((rule.boost || []).join(', '));

  // Keep local edits in sync if the rule is reloaded from the server.
  useEffect(() => {
    setRequiredInput((rule.required || []).join(', '));
    setBoostInput((rule.boost || []).join(', '));
  }, [rule.id, rule.required, rule.boost]);

  const parseTokens = (s) => s.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);

  const saveRequired = () => {
    const next = parseTokens(requiredInput);
    if (JSON.stringify(next) !== JSON.stringify(rule.required || [])) {
      onUpdate(rule.id, { required: next });
    }
  };
  const saveBoost = () => {
    const next = parseTokens(boostInput);
    if (JSON.stringify(next) !== JSON.stringify(rule.boost || [])) {
      onUpdate(rule.id, { boost: next });
    }
  };

  return (
    <li className={`rounded-lg border border-border px-2.5 py-2 text-xs ${rule.active ? '' : 'opacity-50'}`}>
      <div className="flex items-center gap-2">
        <span className={`rounded px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase ${SOURCE_COLORS[rule.source] || SOURCE_COLORS.manual}`}>
          {rule.source}
        </span>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-muted-foreground shrink-0 font-mono text-[0.65rem]">
            {subject ? 'any' : 'all'}
          </span>
          <input
            type="text"
            value={requiredInput}
            onChange={(e) => setRequiredInput(e.target.value)}
            onBlur={saveRequired}
            placeholder={subject ? 'order, #, bestelling' : 'required tokens'}
            className="h-7 flex-1 min-w-0 rounded border border-border bg-white px-2 font-mono text-[0.7rem] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]"
          />
          {!subject && (
            <>
              <span className="text-muted-foreground shrink-0 font-mono text-[0.65rem]">
                boost
              </span>
              <input
                type="text"
                value={boostInput}
                onChange={(e) => setBoostInput(e.target.value)}
                onBlur={saveBoost}
                placeholder="optional boosters"
                className="h-7 flex-1 min-w-0 rounded border border-border bg-white px-2 font-mono text-[0.7rem] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]"
              />
            </>
          )}
        </div>
        <span className="text-muted-foreground shrink-0 text-[0.65rem] tabular-nums">
          {rule.fires_count}×
        </span>
        <label className="inline-flex items-center gap-1 cursor-pointer shrink-0" title="Active">
          <input
            type="checkbox"
            checked={rule.active}
            onChange={(e) => onUpdate(rule.id, { active: e.target.checked })}
            className="h-3.5 w-3.5 rounded border-border"
          />
        </label>
        <button
          type="button"
          onClick={() => onDelete(rule.id)}
          className="rounded p-1 text-rose-600 hover:bg-rose-50 shrink-0"
          title="Delete"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </li>
  );
};

export default Categories;
